import React from "react";
import { ShippingReportUpdate } from "../../../types/shippingReports";

type Props = {
  values: ShippingReportUpdate;
  onChange: (name: keyof ShippingReportUpdate, value: any) => void;
  disabled?: boolean;
};

const inputClass =
  "mt-1 block w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500 disabled:bg-slate-100";

const currentTorontoDateTime = () => {
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone: "America/Toronto",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hourCycle: "h23",
  }).formatToParts(new Date());
  const value = (type: Intl.DateTimeFormatPartTypes) =>
    parts.find((part) => part.type === type)?.value ?? "";
  return `${value("year")}-${value("month")}-${value("day")}T${value("hour")}:${value("minute")}`;
};

function TextField({
  label,
  name,
  value,
  onChange,
  disabled,
  type = "text",
}: {
  label: string;
  name: keyof ShippingReportUpdate;
  value: string | null;
  onChange: Props["onChange"];
  disabled?: boolean;
  type?: string;
}) {
  return (
    <label className="block text-sm font-medium text-slate-700">
      {label}
      <input
        type={type}
        step={type === "number" ? "0.01" : undefined}
        name={name}
        value={value ?? ""}
        disabled={disabled}
        onChange={(event) => onChange(name, event.target.value)}
        className={inputClass}
      />
    </label>
  );
}

function DateTimeField({
  label,
  name,
  value,
  onChange,
  disabled,
}: {
  label: string;
  name: keyof ShippingReportUpdate;
  value: string | null;
  onChange: Props["onChange"];
  disabled?: boolean;
}) {
  return (
    <div>
      <label className="block text-sm font-medium text-slate-700">
        {label}
        <input
          type="datetime-local"
          name={name}
          value={value ?? ""}
          disabled={disabled}
          onChange={(event) => onChange(name, event.target.value || null)}
          className={inputClass}
        />
      </label>
      <div className="mt-1 flex gap-3 text-xs">
        <button
          type="button"
          disabled={disabled}
          className="font-medium text-indigo-700 hover:text-indigo-900 disabled:text-slate-400"
          onClick={() => onChange(name, currentTorontoDateTime())}
        >
          Use current time
        </button>
        {value ? (
          <button
            type="button"
            disabled={disabled}
            className="font-medium text-slate-500 hover:text-slate-700"
            onClick={() => onChange(name, null)}
          >
            Clear
          </button>
        ) : null}
      </div>
    </div>
  );
}

export default function ShippingReportForm({
  values,
  onChange,
  disabled,
}: Props) {
  return (
    <div className="space-y-5">
      <section className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm sm:p-5">
        <h2 className="text-base font-semibold text-slate-900">
          Shipment Information
        </h2>
        <div className="mt-4 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          <TextField label="Customer Name" name="customer_name" value={values.customer_name} onChange={onChange} disabled={disabled} />
          <TextField label="Carrier Name" name="carrier_name" value={values.carrier_name} onChange={onChange} disabled={disabled} />
          <TextField label="Driver Name" name="driver_name" value={values.driver_name} onChange={onChange} disabled={disabled} />
          <TextField label="Trailer Number" name="trailer_number" value={values.trailer_number} onChange={onChange} disabled={disabled} />
          <TextField label="Trailer Plate Number" name="trailer_plate_number" value={values.trailer_plate_number} onChange={onChange} disabled={disabled} />
        </div>
      </section>

      <section className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm sm:p-5">
        <div className="flex items-center justify-between gap-3">
          <h2 className="text-base font-semibold text-slate-900">
            Arrival, Loading and Departure
          </h2>
          <span className="text-xs font-medium text-slate-500">
            America/Toronto
          </span>
        </div>
        <div className="mt-4 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
          <DateTimeField label="Date & Time of Arrival" name="arrival_at" value={values.arrival_at} onChange={onChange} disabled={disabled} />
          <TextField label="Arrival Temperature (°C)" name="arrival_temperature_celsius" value={values.arrival_temperature_celsius} onChange={onChange} disabled={disabled} type="number" />
          <DateTimeField label="Start Time" name="started_at" value={values.started_at} onChange={onChange} disabled={disabled} />
          <DateTimeField label="Completion Time" name="completed_at" value={values.completed_at} onChange={onChange} disabled={disabled} />
          <TextField label="Departure Temperature (°C)" name="departure_temperature_celsius" value={values.departure_temperature_celsius} onChange={onChange} disabled={disabled} type="number" />
          <DateTimeField label="Departure Time" name="departure_at" value={values.departure_at} onChange={onChange} disabled={disabled} />
        </div>
      </section>

      <section className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm sm:p-5">
        <label className="block text-sm font-medium text-slate-700">
          Notes
          <textarea
            value={values.notes}
            disabled={disabled}
            rows={4}
            onChange={(event) => onChange("notes", event.target.value)}
            className={inputClass}
          />
        </label>
      </section>
    </div>
  );
}
